import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  FulfillmentGroupTagSource,
  TagKind,
} from "@redotech/redo-common-model-tags-common/tag";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { AdvancedTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { CreateTagModal } from "@redotech/redo-web/tags/create-tag-modal";
import { useMemo, useState } from "react";
import { augmentFgFiltersWithTagCreate } from "./augment-fg-filters-with-tag-create";

export function useFgAdvancedFiltersWithTagCreate({
  filters,
  onTagsChanged,
}: {
  filters: AdvancedTableFilter[];
  onTagsChanged?: () => void;
}): {
  advancedFilters: AdvancedTableFilter[];
  createTagModal: React.JSX.Element | null;
} {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);
  const [tagCatalogRefreshKey, setTagCatalogRefreshKey] = useState(0);

  const openCreateTagModal = useHandler(() => {
    setCreateTagModalOpen(true);
  });

  const advancedFilters = useMemo(
    () =>
      augmentFgFiltersWithTagCreate(
        filters,
        openCreateTagModal,
        tagCatalogRefreshKey,
      ),
    [filters, openCreateTagModal, tagCatalogRefreshKey],
  );

  const handleCreateTag = useHandler(
    async ({ name, pillTheme }: { name: string; pillTheme: PillTheme }) => {
      await rpcClient.upsertTag({
        tag: {
          name,
          kind: TagKind.FULFILLMENT_GROUP,
          pillTheme,
          source: FulfillmentGroupTagSource.MERCHANT,
        },
      });
      setCreateTagModalOpen(false);
      setTagCatalogRefreshKey((key) => key + 1);
      onTagsChanged?.();
    },
  );

  const createTagModal = createTagModalOpen ? (
    <CreateTagModal
      cancelClicked={() => setCreateTagModalOpen(false)}
      handleCreateTag={handleCreateTag}
    />
  ) : null;

  return { advancedFilters, createTagModal };
}
